import { useState, forwardRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link as RouterLink, NavLink } from 'react-router-dom';
import {
  Divider,
  List,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Collapse,
  Badge,
  Box,
  Link,
  ListItemButton,
  ListItem,
} from '@mui/material';
import { collection, orderBy, onSnapshot, where, query } from 'firebase/firestore';
import {
  DashboardOutlined as DashboardIcon,
  PeopleOutlined as PeopleIcon,
  InfoOutlined as InfoIcon,
  ArrowDropDown as ArrowDown,
  ArrowDropUp as ArrowUp,
  DateRangeOutlined as Calendar,
  DescriptionOutlined as MyRequest,
  InboxOutlined as TeamAdmin,
} from '@mui/icons-material';
import theme from 'theme';
import { snapToArray } from '../../utils';
import { useFirestore } from '../../utils/firebase';
import { useAuth } from '../../auth';

const listItemTitleSx = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  pt: 1,
  pb: 1,
  transition: `background-color ${theme.transitions.main}`,
  '&:hover': {
    backgroundColor: '#F7F7F9',

    '& .MuiListItemText-primary': {
      color: theme.palette.text.secondary,
    },

    '& .MuiSvgIcon-root, & .MuiListItemIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
  '& + &': {
    mt: theme.spacing(1),
  },
};

const hoverRowSx = {
  cursor: 'pointer',
  transition: `background-color ${theme.transitions.main}`,
  '&:hover': {
    backgroundColor: '#F7F7F9',
    '& .MuiListItemText-primary': { color: theme.palette.text.secondary },
    '& .MuiSvgIcon-root, & .MuiListItemIcon-root': { color: theme.palette.text.secondary },
  },
  '& + &': { mt: theme.spacing(1) },
};

const activeRowSx = {
  '&.active': {
    backgroundColor: '#F5FBFF',
    '& .MuiListItemText-primary': { color: theme.palette.primary.main },
    '& .MuiSvgIcon-root, & .MuiListItemIcon-root': { color: theme.palette.primary.main },
  },
};

const nestedRowSx = {
  textIndent: theme.spacing(4),
  '& + &': { mt: 0 },
};

const badgeNumberSx = {
  left: theme.spacing(2),
  textIndent: theme.spacing(0),
  '& .MuiBadge-colorPrimary': {
    color: theme.palette.primary.main,
    backgroundColor: '#C8EAFF',
  },
};

const SidebarNavItem = forwardRef((props, ref) => {
  const { to, sx, className, ...rest } = props;

  return (
    <ListItemButton
      ref={ref}
      component={NavLink}
      to={to}
      className={({ isActive }) => cx(className, isActive && 'active')}
      sx={[hoverRowSx, activeRowSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  );
});
SidebarNavItem.displayName = 'SidebarNavItem';

const SidebarToggleItem = forwardRef((props, ref) => {
  const { onClick, sx, ...rest } = props;

  return (
    <ListItemButton
      ref={ref}
      onClick={onClick}
      sx={[listItemTitleSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  );
});
SidebarToggleItem.displayName = 'SidebarToggleItem';

const eduBudgetCollection = 'edu_budget_requests';
const vacationRequestCollection = 'vacation_requests';
const hardwareRequestCollection = 'hardware_requests';

const Sidebar = ({ className }) => {
  const database = useFirestore();
  const { isManager, isHardwareManager, isOfficeManager, user } = useAuth();
  const [openTeam, setOpenTeam] = useState(false);
  const [openRequests, setOpenRequests] = useState(!isManager);
  const [vacatioRequests, setVacatioRequests] = useState([]);
  const [eduRequests, setEduRequests] = useState([]);
  const [hardwareRequests, setHardwareRequests] = useState([]);

  const handleExpandTeam = () => {
    setOpenTeam(!openTeam);
  };

  const handleExpandRequests = () => {
    setOpenRequests(!openRequests);
  };

  const getPendingRequests = useCallback(
    (collectionName, setRequests) => {
      const q = query(
        collection(database, collectionName),
        where('userTenant', '==', user.tenant.id),
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc'),
      );

      return onSnapshot(
        q,
        snapshot => setRequests(snapToArray(snapshot)),
        error => console.error(`Failed to load pending ${collectionName}`, error),
      );
    },
    [database, user.tenant.id],
  );

  useEffect(() => {
    const canReadTeamVacation = isManager && !isOfficeManager;
    const canReadTeamHardwareEdu = isManager || isHardwareManager;

    const unsubscribes = [];
    if (canReadTeamVacation) {
      unsubscribes.push(getPendingRequests(vacationRequestCollection, setVacatioRequests));
    }
    if (canReadTeamHardwareEdu) {
      unsubscribes.push(getPendingRequests(eduBudgetCollection, setEduRequests));
      unsubscribes.push(getPendingRequests(hardwareRequestCollection, setHardwareRequests));
    }
    return () => unsubscribes.forEach(unsub => unsub());
  }, [getPendingRequests, isManager, isOfficeManager, isHardwareManager]);

  const count = vacatioRequests.length || eduRequests.length || hardwareRequests.length;
  const countVacationRequests = vacatioRequests.length;
  const countEduRequests = eduRequests.length;
  const countHardwareRequests = hardwareRequests.length;

  return (
    <Box
      component="nav"
      className={className}
      sx={{
        backgroundColor: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '63px',
          flexShrink: 0,
          objectFit: 'contain',
          overflow: 'visible',
        }}
      >
        <Link component={RouterLink} to="/" sx={{ fontSize: 0 }}>
          <Box
            component="img"
            src="/images/logo.svg"
            alt="productdock logo"
            sx={{
              cursor: 'pointer',
              width: 33,
              ml: '-81px',
            }}
          />
        </Link>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box component="span" sx={{ fontSize: '21px', marginLeft: '-37px', fontWeight: '600' }}>
            ProductDock
          </Box>
          <Box component="span" sx={{ marginLeft: theme.spacing(-4.5), color: '#686868' }}>
            {user.tenant.name}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(0) }} />
      <List disablePadding>
        <ListItem disablePadding>
          <SidebarNavItem to="/dashboard" data-cy="dashboardSidebar">
            <ListItemIcon sx={{ mr: 0 }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                sx: {
                  fontWeight: 400,
                  color: theme.palette.text.secondary,
                },
              }}
              primary="Dashboard"
            />
          </SidebarNavItem>
        </ListItem>

        <ListItem disablePadding>
          <SidebarNavItem
            to="/calendar "
            sx={{
              display: { xs: 'none', lg: 'flex' }, // hidden xs–md, visible lg+
            }}
          >
            <ListItemIcon sx={{ mr: 0 }}>
              <Calendar />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                sx: {
                  fontWeight: 400,
                  color: theme.palette.text.secondary,
                },
              }}
              primary="Calendar"
            />
          </SidebarNavItem>
        </ListItem>
      </List>
      <Divider sx={{ marginBottom: theme.spacing(1) }} />
      {isManager && (
        <>
          <List
            sx={{ overflow: 'hidden', paddingBottom: 0 }}
            subheader={
              <ListSubheader
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                  textIndent: theme.spacing(0),
                  paddingBottom: theme.spacing(1),
                }}
              >
                Team
              </ListSubheader>
            }
          >
            {!isOfficeManager && (
              <ListItem disablePadding>
                <SidebarNavItem to="/team-members" data-cy="teamMembersSidebar">
                  <ListItemIcon sx={{ mr: 0 }}>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 400,
                        color: theme.palette.text.secondary,
                      },
                    }}
                    primary="Team Members"
                  />
                </SidebarNavItem>
              </ListItem>
            )}
            <ListItem disablePadding>
              <Box sx={{ width: '100%' }}>
                <SidebarToggleItem
                  onClick={handleExpandTeam}
                  sx={listItemTitleSx}
                  data-cy="teamSidebarToggle"
                  aria-expanded={openTeam}
                  aria-controls="team-requests-collapse"
                >
                  <ListItemIcon>
                    <TeamAdmin />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 400,
                        color: theme.palette.text.secondary,
                      },
                    }}
                    primary="Team Requests"
                  />
                  <Badge
                    sx={{
                      '& .MuiBadge-badge': {
                        right: theme.spacing(2),
                      },
                    }}
                    badgeContent={count}
                    color="primary"
                    variant="dot"
                    overlap="rectangular"
                  />
                  {openTeam ? <ArrowUp /> : <ArrowDown />}
                </SidebarToggleItem>
                <Collapse in={openTeam} timeout="auto" unmountOnExit>
                  <List disablePadding id="team-requests-collapse">
                    {!isOfficeManager && (
                      <ListItem disablePadding>
                        <SidebarNavItem
                          to="/team-vacation-requests"
                          data-cy="teamVacationRequestsSidebar"
                          sx={nestedRowSx}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            <ListItemText
                              primaryTypographyProps={{
                                sx: {
                                  fontWeight: 400,
                                  color: theme.palette.text.secondary,
                                },
                              }}
                              primary="Vacation"
                            />
                            <Badge
                              sx={badgeNumberSx}
                              badgeContent={countVacationRequests}
                              color="primary"
                              overlap="rectangular"
                            />
                          </Box>
                        </SidebarNavItem>
                      </ListItem>
                    )}
                    <ListItem disablePadding>
                      <SidebarNavItem
                        to="/team-education-budget-requests"
                        data-cy="teamEducationBudgetSidebar"
                        sx={nestedRowSx}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                              },
                            }}
                            primary="Education Budget"
                          />
                          <Badge
                            sx={badgeNumberSx}
                            badgeContent={countEduRequests}
                            color="primary"
                            overlap="rectangular"
                          />
                        </Box>
                      </SidebarNavItem>
                    </ListItem>
                    <ListItem disablePadding>
                      <SidebarNavItem
                        to="/team-hardware-requests"
                        data-cy="teamHardwareRequestsSidebar"
                        sx={nestedRowSx}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                              },
                            }}
                            primary="Hardware"
                          />
                          <Badge
                            sx={{
                              left: theme.spacing(2),
                              textIndent: theme.spacing(0),
                              '& .MuiBadge-colorPrimary': {
                                color: theme.palette.primary.main,
                                backgroundColor: '#C8EAFF',
                              },
                            }}
                            badgeContent={countHardwareRequests}
                            color="primary"
                            overlap="rectangular"
                          />
                        </Box>
                      </SidebarNavItem>
                    </ListItem>
                  </List>
                </Collapse>
              </Box>
            </ListItem>
          </List>
          <Divider sx={{ marginBottom: theme.spacing(1) }} />
        </>
      )}
      {isHardwareManager && (
        <>
          <List
            sx={{ overflow: 'hidden', paddingBottom: 0 }}
            subheader={
              <ListSubheader
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                  textIndent: theme.spacing(0),
                  paddingBottom: theme.spacing(1),
                }}
              >
                Team
              </ListSubheader>
            }
          >
            <ListItem disablePadding>
              <Box sx={{ width: '100%' }}>
                <SidebarToggleItem
                  onClick={handleExpandTeam}
                  sx={listItemTitleSx}
                  data-cy="teamSidebarToggle"
                  aria-expanded={openTeam}
                  aria-controls="team-requests-collapse"
                >
                  <ListItemIcon>
                    <TeamAdmin />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 400,
                        color: theme.palette.text.secondary,
                      },
                    }}
                    primary="Team Requests"
                  />
                  <Badge
                    sx={{
                      '& .MuiBadge-badge': {
                        right: theme.spacing(2),
                      },
                    }}
                    badgeContent={count}
                    color="primary"
                    variant="dot"
                    overlap="rectangular"
                  />
                  {openTeam ? <ArrowUp /> : <ArrowDown />}
                </SidebarToggleItem>
                <Collapse in={openTeam} timeout="auto" unmountOnExit>
                  <List disablePadding id="team-requests-collapse">
                    <ListItem disablePadding>
                      <SidebarNavItem
                        to="/team-education-budget-requests"
                        data-cy="teamEducationBudgetSidebar"
                        sx={nestedRowSx}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                              },
                            }}
                            primary="Education Budget"
                          />
                          <Badge
                            sx={badgeNumberSx}
                            badgeContent={countEduRequests}
                            color="primary"
                            overlap="rectangular"
                          />
                        </Box>
                      </SidebarNavItem>
                    </ListItem>
                    <ListItem disablePadding>
                      <SidebarNavItem
                        to="/team-hardware-requests"
                        data-cy="teamHardwareRequestsSidebar"
                        sx={nestedRowSx}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                              },
                            }}
                            primary="Hardware"
                          />
                          <Badge
                            sx={badgeNumberSx}
                            badgeContent={countHardwareRequests}
                            color="primary"
                            overlap="rectangular"
                          />
                        </Box>
                      </SidebarNavItem>
                    </ListItem>
                  </List>
                </Collapse>
              </Box>
            </ListItem>
          </List>
          <Divider sx={{ marginBottom: theme.spacing(1) }} />
        </>
      )}
      <List
        sx={{ overflow: 'hidden', paddingBottom: 0 }}
        subheader={
          <ListSubheader
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              textIndent: theme.spacing(0),
              paddingBottom: theme.spacing(1),
            }}
          >
            Requests
          </ListSubheader>
        }
      >
        <ListItem disablePadding>
          <Box sx={{ width: '100%' }}>
            <SidebarToggleItem
              onClick={handleExpandRequests}
              sx={listItemTitleSx}
              data-cy="requestsSidebarToggle"
              aria-expanded={openRequests}
              aria-controls="my-requests-collapse"
            >
              <ListItemIcon>
                <MyRequest />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 400,
                    color: theme.palette.text.secondary,
                  },
                }}
                primary="My Requests"
              />
              {openRequests ? <ArrowUp /> : <ArrowDown />}
            </SidebarToggleItem>
            <Collapse in={openRequests} timeout="auto" unmountOnExit>
              <List disablePadding id="my-requests-collapse">
                <ListItem disablePadding>
                  <SidebarNavItem
                    to="/my-vacation-requests"
                    data-cy="myVacationRequestsSidebar"
                    sx={nestedRowSx}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                        },
                      }}
                      primary="Vacation"
                    />
                  </SidebarNavItem>
                </ListItem>
                <ListItem disablePadding>
                  <SidebarNavItem
                    to="/my-out-of-office"
                    data-cy="outOfOfficeRequestsSidebar"
                    sx={nestedRowSx}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                        },
                      }}
                      primary="Out of Office"
                    />
                  </SidebarNavItem>
                </ListItem>
                <ListItem disablePadding>
                  <SidebarNavItem
                    to="/my-education-budget"
                    data-cy="myEduBudgetRequestsSidebar"
                    sx={nestedRowSx}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                        },
                      }}
                      primary="Education Budget"
                    />
                  </SidebarNavItem>
                </ListItem>
                <ListItem disablePadding>
                  <SidebarNavItem
                    to="/my-hardware-requests"
                    data-cy="myHardwareRequestsSidebar"
                    sx={nestedRowSx}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 400,
                          color: theme.palette.text.secondary,
                        },
                      }}
                      primary="Hardware"
                    />
                  </SidebarNavItem>
                </ListItem>
              </List>
            </Collapse>
          </Box>
        </ListItem>
      </List>
      <Divider sx={{ marginBottom: theme.spacing(1) }} />
      <ListItemButton
        sx={{
          cursor: 'pointer',
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: '#F7F7F9',
            '& .MuiListItemText-root, & .MuiListItemIcon-root': {
              color: theme.palette.text.secondary,
            },
          },
          '& + &': {
            marginTop: theme.spacing(1),
          },
        }}
        component="a"
        href="https://productdockworkspace.slack.com/archives/C03KGGQM19B"
        target="_blank"
      >
        <ListItemIcon sx={{ marginRight: 0 }}>
          <InfoIcon />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            sx: {
              fontWeight: 400,
              color: theme.palette.text.secondary,
            },
          }}
          primary="Help & Support"
        />
      </ListItemButton>
    </Box>
  );
};

Sidebar.propTypes = {
  className: PropTypes.string,
};

export default Sidebar;
